import { Inject, Injectable, Optional } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router } from '@angular/router';
import { Observable, of, merge, iif } from 'rxjs';
import { PermissionsService } from '../services/permissions.service';
import { switchMap } from 'rxjs/operators';

@Injectable()
export class PermissionsGuard implements CanActivate {
  defaultRedirectUrl = '/';
  permissionFormatError =
    'Invalid route permission format. Format must be [route, permission] or [route, permission, paramTypes]';

  companyParam = 'companyId';
  divisionParam = 'divisionId';
  appParam = 'appId';
  paramTypeError = `Invalid param type. Type must be one of ${this.companyParam}, ${this.divisionParam}, ${this.appParam}`;

  constructor(
    public permissionService: PermissionsService,
    private router: Router,
    @Inject('routePermissions') private readonly routePermissions,
    @Optional() @Inject('redirectUrl') private readonly redirectUrl: string
  ) {
    this.redirectUrl = this.redirectUrl ? this.redirectUrl : this.defaultRedirectUrl;
  }

  _getFullPath(snapshot: ActivatedRouteSnapshot, currentPath: string): string {
    if (!snapshot.parent || !snapshot.parent.routeConfig) {
      return currentPath || '/';
    }
    return this._getFullPath(snapshot.parent, [snapshot.parent.routeConfig.path, currentPath].join('/')) || '/';
  }

  _hasPermission(configPath: string, snapshot: ActivatedRouteSnapshot): Observable<any> {
    let guardedRoute;
    let permission;
    let paramTypes;

    for (const routePerm of this.routePermissions) {
      if (routePerm.length !== 2 && routePerm.length !== 3) {
        throw new Error(this.permissionFormatError);
      }
      if (routePerm.length === 2) {
        [guardedRoute, permission] = routePerm;
      } else {
        [guardedRoute, permission, paramTypes] = routePerm;
      }

      // If parameters are in the guardedRoute that should be guarded, re-build the permission string to specify the
      // companyId, divisionId or appId specific to the activated route before checking the perm.
      if (paramTypes) {
        const parts = this.permissionService.parsePermission(permission);
        const [action, resource, appPrefix] = parts;
        let [, , , divisionId, companyId, applicationId] = parts;

        for (const [param, type] of Object.entries(paramTypes)) {
          switch (type) {
            case this.companyParam:
              companyId = snapshot.params[param];
              break;
            case this.divisionParam:
              divisionId = snapshot.params[param];
              break;
            case this.appParam:
              applicationId = snapshot.params[param];
              break;
            default:
              throw new Error(this.paramTypeError);
          }
        }
        permission = [applicationId, companyId, divisionId, appPrefix, action, resource].join(':');
      }

      if (configPath === guardedRoute) {
        // In case permissionService emit false value, return redirectUrl.
        return merge(
          this.permissionService.hasPermission(permission),
          this.permissionService.isfetchPermsSuccess()
        ).pipe(
          switchMap(hasPerm => {
            return iif(() => hasPerm, of(true), of(this.router.parseUrl(this.redirectUrl)));
          })
        );
      }
    }

    return of(this.router.parseUrl(this.redirectUrl));
  }

  canActivate(snapshot: ActivatedRouteSnapshot): Observable<any> {
    const path = this._getFullPath(snapshot, snapshot.routeConfig.path);
    return this._hasPermission(path, snapshot);
  }
}
